import type { FunctionComponent } from 'react';

import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';

import styles from './MetricFacet.module.less';

type MetricFacetComponent = FunctionComponent & {
  Label: FunctionComponent;
  Body: FunctionComponent;
  Legend: FunctionComponent;
  Total: FunctionComponent;
};

/**
 * A reusable MetricFacet component for showing several related measures and graphics.
 *
 * @example
 * <MetricFacet>
 *  <MetricFacet.Total><Icon icon={icons.goodIcon} /> 123</MetricFacet.Label>
 *  <MetricFacet.Label><Icon icon={icons.goodIcon} /> My Cool Graphic</MetricFacet.Label>
 *  <MetricFacet.Body><SomeCoolGraphic /></MetricFacet.Body>
 *  <MetricFacet.Legend>Some explanatory text and/or a proper legend</MetricFacet.Legend>
 * </MetricFacet>
 */
const MetricFacet: MetricFacetComponent = ({ children }) => {
  const total = findFirstForSlot('total', children);
  const label = findFirstForSlot('label', children);
  const body = findFirstForSlot('body', children);
  const legend = findAllForSlot('legend', children);
  return (
    <div className={styles.metricFacet}>
      <div className={styles.metric}>
        <div className={styles.topLine}>
          <div className={styles.label}>{label}</div>
          {total !== undefined && <span className={styles.total}>{total}</span>}
        </div>
        {body}
      </div>
      {legend ?? null}
    </div>
  );
};

MetricFacet.Label = defineSlotComponent('label', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
MetricFacet.Label.displayName = 'MetricFacet.Label';

MetricFacet.Body = defineSlotComponent('body', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
MetricFacet.Body.displayName = 'MetricFacet.Body';

MetricFacet.Legend = defineSlotComponent('legend', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
MetricFacet.Legend.displayName = 'MetricFacet.Legend';

MetricFacet.Total = defineSlotComponent('total', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
MetricFacet.Total.displayName = 'MetricFacet.Total';

export default MetricFacet;
